import React, { useEffect, useState } from "react";
import { BellOutlined } from "@ant-design/icons";
import { Badge, Dropdown, List, Modal, Spin, Tabs } from "antd";
import axiosApiInstance from "../../axios/axiosinstance";
import i18n, { t } from "i18next";
import { EntNotificationsFilter } from "../../models/filters/notificationsFilter";
import moment from "moment";
import styles from "./styles.module.css";

type Notification = {
  id: number;
  title: string;
  description: string;
  read: boolean;
  date: string;
  fullDate: string;
};

export const Notifications = () => {
  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);
  const [readNotifications, setReadNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);

  const filter = new EntNotificationsFilter();
  filter.page = 1;
  filter.size = 10;
  filter.isRead = false;

  const filterRead = new EntNotificationsFilter();
  filterRead.page = 1;
  filterRead.size = 10;
  filterRead.isRead = true;

  useEffect(() => {
	fetchNotifications(filter);
	fetchNotificationsRead(filterRead);

	const intervalId = setInterval(() => {
	  fetchNotifications(filter);
	  fetchNotificationsRead(filterRead);
	}, 30000);

	return () => clearInterval(intervalId);
  }, [filter.page, filterRead.page]);

  const fetchNotifications = async (filter: EntNotificationsFilter) => {
	setLoading(true);
	try {
	  const { data } = await axiosApiInstance.post("/notifications/get-all", {
		page: filter.page,
		size: filter.size,
		isRead: filter.isRead,
	  });

	  setUnreadNotifications(
		data.res.content.map((item: any) => ({
		  id: item.id,
		  title: i18n.language === "ru" ? item?.titleRu : item?.titleKy,
		  description: i18n.language === "ru" ? item?.descriptionRu : item?.descriptionKy,
		  read: item.isRead,
		  date: moment(item.createdAt).fromNow(),
		  fullDate: moment(item.createdAt).format("DD.MM.YYYY HH:mm"),
		})) || []
	  );
	} catch (error) {
	  console.error(t("msg.error_download_msg"), error);
	} finally {
	  setLoading(false);
	}
  };

  const fetchNotificationsRead = async (filterRead: EntNotificationsFilter) => {
	setLoading(true);
	try {
	  const { data } = await axiosApiInstance.post("/notifications/get-all", {
		page: filterRead.page,
		size: filterRead.size,
		isRead: filterRead.isRead,
	  });

	  setReadNotifications(
		data.res.content.map((item: any) => ({
		  id: item.id,
		  title: i18n.language === "ru" ? item?.titleRu : item?.titleKy,
		  description: i18n.language === "ru" ? item?.descriptionRu : item?.descriptionKy,
		  read: item.isRead,
		  date: moment(item.createdAt).fromNow(),
		  fullDate: moment(item.createdAt).format("DD.MM.YYYY HH:mm"),
		})) || []
	  );
	} catch (error) {
	  console.error(t("msg.error_download_msg"), error);
	} finally {
	  setLoading(false);
	}
  };

  const markAsRead = async (id: number) => {
	try {
	  await axiosApiInstance.post(`/notifications/set-read/${id}`);

	  const notificationToMark = unreadNotifications.find((notif) => notif.id === id);
	  if (notificationToMark) {
		setUnreadNotifications((prev) =>
		  prev.filter((notif) => notif.id !== id)
		);
		setReadNotifications((prev) => [
		  ...prev,
		  { ...notificationToMark, read: true },
		]);
	  }
	} catch (error) {
	  console.error(t("msg.error_update_msg"), error);
	}
  };


  const openModal = (notification: Notification) => {
	setSelectedNotification(notification);
	setModalVisible(true);
	markAsRead(notification.id);
  };

  const menu = (
	<div
	  style={{
		width: 300,
		padding: 6,
		background: "white",
		borderRadius: 4,
		boxShadow: "0 6px 16px 0 rgba(0, 0, 0, 0.12)",
		marginTop: 20,
	  }}
	>
	  {loading ? (
		<Spin />
	  ) : unreadNotifications.length || readNotifications.length ? (
		<Tabs defaultActiveKey="1">
		  <Tabs.TabPane tab="Новые" key="1">
			<div style={{ maxHeight: "300px", overflowY: "auto" }}>
			  <List
				dataSource={unreadNotifications}
				renderItem={(item) => (
				  <List.Item
					onClick={() => openModal(item)}
					className={styles.notificationItem}
				  >
					<div style={{ display: "flex", alignItems: "center" }}>
					  <span className={styles.notificationCircle} />
					  <div>
						<strong className={styles.notificationTitle}>{item.title}</strong>
						<br />
						<small className={styles.notificationDate}>{item.fullDate}</small>
					  </div>
					</div>
				  </List.Item>
				)}
			  />
			</div>
		  </Tabs.TabPane>
		  <Tabs.TabPane tab="Просмотренные" key="2">
			<div style={{ maxHeight: "300px", overflowY: "auto" }}>
			  <List
				dataSource={readNotifications}
				renderItem={(item) => (
				  <List.Item
					onClick={() => openModal(item)}
					className={`${styles.notificationItem} ${styles.readNotification}`}
					style={{ cursor: "pointer" }}
				  >
					<div style={{ display: "flex", alignItems: "center" }}>
					  <div>
						<strong className={styles.notificationTitle}>{item.title}</strong>
						<br />
						<small className={styles.notificationDate}>{item.fullDate}</small>
					  </div>
					</div>
				  </List.Item>
				)}
			  />
			</div>
		  </Tabs.TabPane>
		</Tabs>
	  ) : (
		<p>{t("msg.no_message") as string}</p>
	  )}
	</div>
  );

  return (
	<>
	  <Dropdown overlay={menu} trigger={["click"]}>
		<Badge count={unreadNotifications.length}>
		  <BellOutlined
			style={{
			  fontSize: "24px",
			  cursor: "pointer",
			  transition: "color 0.3s",
			  marginLeft: "20px",
			}}
		  />
		</Badge>
	  </Dropdown>
	  <Modal
		title={selectedNotification?.title}
		open={modalVisible}
		onCancel={() => setModalVisible(false)}
		footer={null}
	  >
		<p>{selectedNotification?.description}</p>
		<small style={{ color: "#888" }}>
		  Получено: {selectedNotification?.fullDate}
		</small>
	  </Modal>
	</>
  );
};
